import { BrowserRouter, Routes, Route } from "react-router-dom";
import React, { lazy, Suspense, useEffect, useRef, useState } from "react";
import "./index.css";
import Home from "./Pages/Home";
import AnimatedBackground from "./components/Background";
import Navbar from "./components/Navbar";
import ThemeToggle from "./components/ThemeToggle";
import HashScroll from "./components/HashScroll";
import ScrollToTop from "./components/ScrollToTop";

const About = lazy(() => import("./Pages/About"));
const Portofolio = lazy(() => import("./Pages/Portofolio"));
const Publications = lazy(() => import("./components/Publications"));
const Achievements = lazy(() => import("./components/Achievements"));
const Leadership = lazy(() => import("./components/Leadership"));
const ContactPage = lazy(() => import("./Pages/Contact"));
const ProjectDetails = lazy(() => import("./components/ProjectDetail"));
const NotFound = lazy(() => import("./Pages/NotFound"));

const SectionFallback = () => <div className="min-h-[40vh]" aria-hidden="true" />;

const DeferredSection = ({ id, children, minHeight = "60vh" }) => {
  const containerRef = useRef(null);
  // Also match sub-path hashes like #Portofolio/certifications
  const [isVisible, setIsVisible] = useState(() => window.location.hash.startsWith(`#${id}`));

  useEffect(() => {
    if (isVisible || !containerRef.current) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "-100px 0px", threshold: 0.01 }
    );
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [isVisible]);

  return (
    <div ref={containerRef} id={isVisible ? undefined : id} style={{ minHeight }}>
      {isVisible ? children : null}
    </div>
  );
};

const LandingPage = () => {
  return (
    <>
      <HashScroll enabled />
      <Navbar />
      <AnimatedBackground />
      <ThemeToggle />
      <main id="main-content">
        <Home />
        <DeferredSection id="About"><Suspense fallback={<SectionFallback />}><About /></Suspense></DeferredSection>
        <DeferredSection id="Publications"><Suspense fallback={<SectionFallback />}><Publications /></Suspense></DeferredSection>
        <DeferredSection id="Portofolio"><Suspense fallback={<SectionFallback />}><Portofolio /></Suspense></DeferredSection>
        <DeferredSection id="Achievements"><Suspense fallback={<SectionFallback />}><Achievements /></Suspense></DeferredSection>
        <DeferredSection id="Leadership"><Suspense fallback={<SectionFallback />}><Leadership /></Suspense></DeferredSection>
        <DeferredSection id="Contact"><Suspense fallback={<SectionFallback />}><ContactPage /></Suspense></DeferredSection>
      </main>
    </>
  );
};

const ProjectPageLayout = () => (
  <>
    <ThemeToggle />
    <Suspense fallback={<SectionFallback />}>
      <ProjectDetails />
    </Suspense>
    <footer className="w-full border-t border-border-light dark:border-border-dark bg-surface-light dark:bg-surface-dark py-8 mt-12">
      <div className="max-w-7xl mx-auto px-6 text-center text-sm text-text-secondary-light dark:text-text-secondary-dark font-mono">
        <span>© 2026 Ifaz Ikram. Built with engineering precision.</span>
      </div>
    </footer>
  </>
);

function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <Routes>
        <Route
          path="/"
          element={<LandingPage />}
        />
        <Route path="/project/:id" element={<ProjectPageLayout />} />
        <Route path="*" element={<Suspense fallback={<SectionFallback />}><NotFound /></Suspense>} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
